import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Optional } from '@angular/core';

import { CookieOptions as OrgCookieOptions } from 'express';

import { parseJsonString } from '../helpers';
import { SERVER_REQUEST, SERVER_RESPONSE, ServerRequest, ServerResponse } from '../server';

export type CookieOptions = Omit<OrgCookieOptions, 'encode'>;

export const COOKIE_MAX_AGE_1YEAR = 60 * 60 * 24 * 365;

function parseCookies(cookie: string): Record<string, unknown> {
  return decodeURIComponent(cookie || '')
    .split(';')
    .reduce((acc, curr) => {
      if (curr) {
        const parts = curr.split('=');
        return {
          ...acc,
          [parts[0].trim()]: parseJsonString((parts[1] || '').trim())
        };
      } else {
        return acc;
      }
    }, {});
}

function stringifyValue(value: unknown): string {
  return typeof value === 'object' ? JSON.stringify(value) : (value as string);
}

function stringifyOptions(cookieOptions: CookieOptions): string {
  const { maxAge, ...rest } = cookieOptions;
  const options = { ...rest, ...(maxAge >= 0 ? { 'max-age': maxAge } : null) };
  const getKeyVal = (key: string): string => (options[key] === true ? key : `${key}=${options[key]}`);
  return Object.keys(options).reduce((acc, curr) => `${acc} ${getKeyVal(curr)};`, '');
}

@Injectable({
  providedIn: 'root'
})
export class CookieService {
  constructor(
    @Optional() @Inject(SERVER_REQUEST) private readonly request: ServerRequest,
    @Optional() @Inject(SERVER_RESPONSE) private readonly response: ServerResponse,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  getAll(): Record<string, unknown> {
    return parseCookies(this.getCookieHeader());
  }

  get<T = unknown>(name: string): T {
    return this.getAll()[name] as T;
  }

  set(name: string, value: unknown, cookieOptions?: CookieOptions): void {
    const options = { path: '/', ...cookieOptions };
    if (this.response) {
      // Express expects maxAge to be in milliseconds
      options.maxAge &&= options.maxAge * 1000;
      this.response.cookie(name, JSON.stringify(value), options);
    } else {
      this.document.cookie = `${name}=${encodeURIComponent(stringifyValue(value))};${stringifyOptions(options)}`;
    }
  }

  delete(name: string, path = '/'): void {
    this.response ? this.response.clearCookie(name) : this.set(name, '', { maxAge: 0, path });
  }

  private getCookieHeader(): string {
    return this.request ? this.request.headers.cookie : this.document.cookie;
  }
}
